import { Check, Lock, PenLine, Play, Sparkle } from "lucide-react";

import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";

/**
 * The three things a grown-up can say about a skill, in the words they think
 * in. They map onto the two columns that already exist — locked, open, and open
 * past the mastery threshold — rather than adding a fourth state to keep in
 * sync. See SkillSetup on the server.
 */
export const SKILL_STATUSES = ["not_yet", "learning", "known"] as const;
export type SkillStatus = (typeof SKILL_STATUSES)[number];

export const STATUS_META: Record<
  SkillStatus,
  { label: string; short: string; hint: string; icon: typeof Lock; className: string }
> = {
  not_yet: {
    label: "Dar ne",
    short: "Dar ne",
    hint: "Užrakinta — šito dar neduodame",
    icon: Lock,
    className: "data-[on=true]:bg-muted-foreground/15 data-[on=true]:text-foreground",
  },
  learning: {
    label: "Mokomės",
    short: "Mokomės",
    hint: "Atrakinta — sesijos gali duoti šių užduočių",
    icon: Play,
    className: "data-[on=true]:bg-blue-500/15 data-[on=true]:text-blue-700 dark:data-[on=true]:text-blue-300",
  },
  known: {
    label: "Jau moka",
    short: "Moka",
    hint: "Laikome įvaldytu — atrakina tolesnius įgūdžius",
    icon: Check,
    className:
      "data-[on=true]:bg-emerald-500/15 data-[on=true]:text-emerald-700 dark:data-[on=true]:text-emerald-300",
  },
};

/**
 * One control for the whole question "where does this child stand on this
 * skill", sitting on the skill itself.
 *
 * It used to take five steps behind a pencil icon — enter edit mode, filter to
 * a grade, tick a checkbox, open the level input, type a number — which is why
 * marking a single skill was something nobody did.
 */
export function SkillStatusControl({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: SkillStatus;
  onChange: (next: SkillStatus) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        "inline-flex shrink-0 overflow-hidden rounded-md border bg-background",
        disabled && "opacity-50"
      )}
      role="group"
    >
      {SKILL_STATUSES.map((status) => {
        const meta = STATUS_META[status];
        const Icon = meta.icon;
        const on = value === status;

        return (
          <Tooltip key={status}>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-on={on}
                aria-pressed={on}
                aria-label={meta.label}
                disabled={disabled}
                onClick={() => !on && onChange(status)}
                className={cn(
                  "flex items-center gap-1 border-r px-2 py-1 text-xs transition-colors last:border-r-0",
                  "text-muted-foreground/70 hover:bg-muted disabled:cursor-not-allowed",
                  meta.className,
                  on && "font-medium"
                )}
              >
                <Icon className="h-3 w-3 shrink-0" />
                {/* The label is dropped on a row but kept on the active
                    segment, so a list of five hundred rows still reads as
                    words rather than as a wall of icons. */}
                {(!compact || on) && <span>{compact ? meta.short : meta.label}</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent>{meta.hint}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * Marks a percentage that came from a grown-up rather than from the child.
 * Without it a hand-marked 100% is indistinguishable from one that was earned,
 * and the gradebook quietly starts lying about what has been demonstrated.
 */
export function ManualMarkBadge({ className }: { className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded border border-dashed px-1.5 py-0.5",
            "text-[10px] text-muted-foreground",
            className
          )}
        >
          <PenLine className="h-2.5 w-2.5" />
          pažymėta
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Pažymėta ranka — mokinys šio įgūdžio užduočių dar nesprendė
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Marks a standing the app guessed from the learner's grade when they first
 * opened the subject — see SkillSetup.assume_subject! on the server.
 *
 * It is deliberately not the „pažymėta" badge: that one says a person looked at
 * this skill and decided, and putting it on a number derived from a birth year
 * would have the gradebook claim a decision nobody made. Sessions treat these
 * more carefully too — they are probed at level 2 rather than at the level the
 * percentage would choose — and the mark clears itself as soon as anything
 * measures the skill or anybody confirms it.
 */
export function AssumedMarkBadge({ className }: { className?: string }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 rounded border border-dashed px-1.5 py-0.5",
            "text-[10px] text-amber-700 dark:text-amber-300",
            className
          )}
        >
          <Sparkle className="h-2.5 w-2.5" />
          pagal klasę
        </span>
      </TooltipTrigger>
      <TooltipContent>
        Spėta pagal klasę, kai mokinys pradėjo šį dalyką — niekas dar nepatikrino.
        Patikra arba „ką jau moka" nustatymas tai patvirtina.
      </TooltipContent>
    </Tooltip>
  );
}
